<template>
  <div
    class="accordion"
    :class="{'expandable': !expandAll}"
  >
    <template
      v-for="(item, index) of items"
      :key="`${section}-${index}`"
    >
      <div
        v-if="containerSizeClasses && mobileOnly(item)"
        :class="containerSizeClasses"
      >
        <div
          :ref="`${ section }-${ index }`"
          class="accordion__item"
          :class="{[$class('active')]: activeItems.includes(`${ section }-${ index }`)}"
        >
          <div
            :class="`accordion__title ${titleClass}`"
            :aria-controls="`content-${ index }`"
            :aria-expanded="activeItems.includes(`${ section }-${ index }`)"
          >
            <span
              v-if="expandAll"
              v-text="item.title"
            />
            <button
              v-if="!expandAll"
              :id="`Accordion-${id}${ index }`"
              class="accordion__control"
              type="button"
              :class="buttonClass"
              :aria-controls="`content-${ index }`"
              aria-expanded="false"
              :aria-label="item.title"
              @click.prevent="() => toggleItem(`${ section }-${ index }`)"
            >
              <span
                :class="labelClass"
                v-text="item.title"
              />
              <icon-minus />
              <icon-plus />
            </button>
          </div>

          <accordion-item
            :is-active="activeItems.includes(`${ section }-${ index }`)"
            :item="item"
            :index="index"
            :expand-all="expandAll"
          >
            <slot :item="item" />
          </accordion-item>
        </div>
      </div>

      <div
        v-else-if="mobileOnly(item)"
        :ref="`${ section }-${ index }`"
        class="accordion__item"
        :class="{[$class('active')]: activeItems.includes(`${ section }-${ index }`)}"
      >
        <div
          :class="`accordion__title ${titleClass}`"
          :aria-controls="`content-${ index }`"
          :aria-expanded="activeItems.includes(`${ section }-${ index }`)"
        >
          <span
            v-if="expandAll"
            v-text="item.title"
          />
          <button
            v-if="!expandAll"
            :id="`Accordion-${id}${ index }`"
            class="accordion__control"
            type="button"
            :class="buttonClass"
            :aria-controls="`content-${ index }`"
            aria-expanded="false"
            :aria-label="item.title"
            @click.prevent="() => toggleItem(`${ section }-${ index }`)"
          >
            <span
              :class="labelClass"
              v-text="item.title"
            />
            <icon-minus />
            <icon-plus />
          </button>
        </div>

        <accordion-item
          :is-active="activeItems.includes(`${ section }-${ index }`)"
          :item="item"
          :index="index"
          :expand-all="expandAll"
        >
          <slot :item="item" />
        </accordion-item>
      </div>
    </template>

    <slot />
  </div>
</template>

<script>

/**
 * Vue: accordion (accordion)
 * -----------------------------------------------------------------------------
 * Accordion.
 *
 */
import { mapState } from 'vuex'
import IconPlus from '~icons/directional-navigation/plus.svg'
import IconMinus from '~icons/directional-navigation/minus.svg'
import AccordionItem from '~global/accordion/accordion-item'
import breakpoints from '~/config/breakpoints'
import timings from '~/config/timings'

export default {
  name: 'AccordionItems',

  components: {
    AccordionItem,
    IconMinus,
    IconPlus,
  },

  props: {
    buttonClass: {
      type: String,
      default: '',
    },
    containerSizeClasses: {
      type: String,
      default: '',
    },
    expandAll: {
      type: Boolean,
      default: false,
    },
    expandLimit: {
      type: [Boolean, Number],
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    section: {
      type: String,
      default: '',
    },
    titleClass: {
      type: String,
      default: 'text-heading-s',
    },
    labelClass: {
      type: String,
      default: 'text-heading-s',
    },
    id: {
      type: String,
      default: '',
    },
    scrollTo: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      global: true,
      settings: {
        singleOpen: true,
        expandInitial: false,
        expandAll: false,
        animate: true,
      },
      activeItems: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Compture tablet size.
     */
    isTablet() {
      return this.breakpoint === 'm'
    },
  },

  mounted() {
    if (this.expandAll || this.expandLimit) {
      this.addActiveItems()
    }
  },

  methods: {

    /**
     * Check if the accordion item should only display on mobile.
     * @param {Object} item - accordion item
     * @returns {Boolean}
     */
    mobileOnly(item) {
      if (item?.mobileOnly) {
        if (!this.isMobile || this.isTablet) {
          return false
        }
      }

      return true
    },

    /**
     * Check if the click target is an accordion control.
     * @param {HTMLElement} target - Clicked target to check quick view
     * attributes against.
     * @returns {Boolean}
     */
    isTargetControl(target) {
      return (
        typeof target.attributes['aria-controls'] !== 'undefined'
      )
    },

    /**
     * Toggle targeted accordion item.
     * @param {HTMLElement} control - The clicked accordion control.
     */
    toggleItem(control) {
      const item = this.$refs[control]
      if (this.isItemExpanded(control)) {
        const { activeItems } = this
        const itemIndex = activeItems.indexOf(control)
        activeItems.splice(itemIndex, 1)

      } else {
        this.activeItems.push(control)
      }

      if (this.scrollTo) {
        this.setWindowScroll(item[0])
      }
    },

    /**
     * Check if item is active.
     * @param {HTMLElement} control - The accordion item.
     * @returns {Boolean}
     */
    isItemExpanded(control) {
      return this.activeItems.includes(control)
    },

    addActiveItems() {
      this.items.forEach((el, index) => {
        if (this.expandAll || (this.expandLimit && index < this.expandLimit)) {
          this.activeItems.push(`${this.section}-${index}`)
        }
      })
    },

    /**
     * Scroll to element if off screen
     * @param {Object} target - The returned element that was clicked
     */
    setWindowScroll(target) {
      if (!window.matchMedia(`(min-width: ${breakpoints.l})`).matches) {
        setTimeout(() => {
          if (target.getBoundingClientRect().top < 0) {
            const headerOffset = 50
            const elementPosition = target.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.scrollY - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })
          }
        }, timings.slow)
      }
    },
  },
}
</script>
